import { useState, useMemo } from "react"
import { Users, Calendar, Clock, Star, CheckCircle2, ChevronRight, Briefcase, X, Send } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

// ─── Mock mentor data (prototype) ─────────────────────────────────────────────
const MOCK_MENTORS = [
  {
    id: "m1",
    name: "Dr. Amara Osei",
    title: "Senior Software Engineer",
    company: "Google",
    bio: "10+ years building scalable systems. Passionate about helping African students break into big tech.",
    expertise: ["Computer Science", "Software Engineering", "Cloud Infrastructure"],
    paths: ["broad_stem_tech"],
    availability: "Weekends · 2 slots/week",
    rating: 4.9,
    sessionsCompleted: 142,
    avatar: "A",
    color: "bg-indigo-600",
  },
  {
    id: "m2",
    name: "Prof. Ngozi Adeyemi",
    title: "Consultant Physician & Academic",
    company: "UNILAG Teaching Hospital",
    bio: "Specialist in Internal Medicine. Mentors pre-med students through MBBS and residency applications.",
    expertise: ["Medicine", "Healthcare", "Medical Research"],
    paths: ["broad_health_medicine"],
    availability: "Tuesdays & Thursdays · 1 slot/day",
    rating: 4.8,
    sessionsCompleted: 88,
    avatar: "N",
    color: "bg-emerald-600",
  },
  {
    id: "m3",
    name: "Tunde Bakare",
    title: "Investment Banker",
    company: "Stanbic IBTC",
    bio: "CFA charterholder with 8 years in financial markets. Helps students navigate ICAN, ACCA, and finance careers.",
    expertise: ["Finance", "Investment Banking", "Accounting"],
    paths: ["broad_business_finance"],
    availability: "Mondays · 3 slots/week",
    rating: 4.7,
    sessionsCompleted: 65,
    avatar: "T",
    color: "bg-amber-600",
  },
  {
    id: "m4",
    name: "Adaeze Nwosu",
    title: "Creative Director",
    company: "DDB Lagos",
    bio: "Award-winning designer with experience at Publicis and Ogilvy. Mentors emerging creatives on portfolio and agency life.",
    expertise: ["Graphic Design", "Advertising", "Branding"],
    paths: ["broad_creative_arts"],
    availability: "Fridays · 2 slots/week",
    rating: 4.9,
    sessionsCompleted: 51,
    avatar: "A",
    color: "bg-rose-600",
  },
  {
    id: "m5",
    name: "Barrister Emeka Eze",
    title: "Senior Partner",
    company: "Eze & Associates",
    bio: "Called to the Nigerian Bar in 2008. Specialises in corporate law. Mentors law students on bar exams and firm recruitment.",
    expertise: ["Law", "Corporate Law", "Litigation"],
    paths: ["broad_law_social"],
    availability: "Wednesdays · 2 slots/week",
    rating: 4.6,
    sessionsCompleted: 34,
    avatar: "E",
    color: "bg-violet-600",
  },
]

function RequestModal({ mentor, studentName, onClose, onSubmit }) {
  const [note, setNote] = useState("")
  const [topic, setTopic] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = () => {
    onSubmit({ mentorId: mentor.id, note, topic })
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center space-y-4">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h3 className="text-xl font-black text-slate-900">Request Sent!</h3>
          <p className="text-slate-500 text-sm">
            <strong>{mentor.name}</strong> will be notified. Expect a response within 2 business days.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-black text-slate-900 text-lg">Request a Session</h3>
            <p className="text-slate-500 text-sm">with {mentor.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5 block">Topic / Goal</label>
          <input
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder="e.g. UTME subject choice for Computer Science"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5 block">Message to Mentor</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={4}
            placeholder={`Hi ${mentor.name.split(" ")[0]}, I'd love to connect about...`}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-slate-200 text-slate-600 font-semibold py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!topic.trim()}
            className="flex-1 bg-indigo-600 text-white font-bold py-2.5 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Send Request
          </button>
        </div>
      </div>
    </div>
  )
}

function MentorCard({ mentor, onRequest }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full ${mentor.color} text-white font-black text-lg flex items-center justify-center shrink-0`}>
          {mentor.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-slate-900">{mentor.name}</p>
          <p className="text-sm text-slate-500">{mentor.title} · {mentor.company}</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1 text-xs text-amber-600 font-semibold">
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
              {mentor.rating}
            </span>
            <span className="text-xs text-slate-400">{mentor.sessionsCompleted} sessions</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-600 mt-3 leading-relaxed">{mentor.bio}</p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {mentor.expertise.map(e => (
          <span key={e} className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
            {e}
          </span>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Clock className="w-3.5 h-3.5" />
          {mentor.availability}
        </div>
        <button
          onClick={() => onRequest(mentor)}
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
        >
          <Calendar className="w-3.5 h-3.5" />
          Request Session
        </button>
      </div>
    </div>
  )
}

export default function MentorBookingPage() {
  const [modal, setModal] = useState(null)
  const [mySessions, setMySessions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mentor-sessions") ?? "[]") } catch { return [] }
  })

  const { studentName, selectedPaths } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    return {
      studentName: student ? `${student.firstName ?? student.name ?? "Student"} ${student.lastName ?? ""}`.trim() : "Student",
      selectedPaths: student?.selectedBroadPaths ?? [],
    }
  }, [])

  const mentors = useMemo(() => {
    if (selectedPaths.length === 0) return MOCK_MENTORS
    const relevant = MOCK_MENTORS.filter(m => m.paths.some(p => selectedPaths.includes(p)))
    const others = MOCK_MENTORS.filter(m => !m.paths.some(p => selectedPaths.includes(p)))
    return [...relevant, ...others]
  }, [selectedPaths])

  const handleSubmit = ({ mentorId, note, topic }) => {
    const session = { mentorId, note, topic, status: "pending", requestedAt: new Date().toISOString() }
    const updated = [...mySessions, session]
    setMySessions(updated)
    localStorage.setItem("mentor-sessions", JSON.stringify(updated))
  }

  const pendingCount = mySessions.filter(s => s.status === "pending").length

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-black text-slate-900">Career Mentors</h1>
                <p className="text-slate-500 text-sm mt-1">Connect with professionals in your fields of interest.</p>
              </div>
              {pendingCount > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 text-indigo-700 text-sm font-semibold px-4 py-2 rounded-xl">
                  {pendingCount} pending request{pendingCount !== 1 ? "s" : ""}
                </div>
              )}
            </div>

            {/* My sessions strip */}
            {mySessions.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">My Requests</p>
                {mySessions.slice(-3).map((s, i) => {
                  const mentor = MOCK_MENTORS.find(m => m.id === s.mentorId)
                  return (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className={`w-6 h-6 rounded-full ${mentor?.color ?? "bg-slate-400"} text-white text-xs font-bold flex items-center justify-center shrink-0`}>
                        {mentor?.avatar ?? "?"}
                      </div>
                      <span className="font-semibold text-slate-700">{mentor?.name ?? "Unknown"}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500 truncate flex-1">{s.topic}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.status === "pending" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                        {s.status}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Mentor grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {mentors.map(m => (
                <MentorCard key={m.id} mentor={m} onRequest={setModal} />
              ))}
            </div>

          </div>
        </main>
      </div>

      {modal && (
        <RequestModal
          mentor={modal}
          studentName={studentName}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
        />
      )}
    </ProtectedRoute>
  )
}
